import { formatDateLocaleMonth } from "@transfr-inc/dashboard-components/utils";
import clsx from "clsx";
import { useQueryStates } from "nuqs";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { Loader } from "@transfr-inc/dashboard-components";

import { FeedbackButton } from "@components/feedback-button";
import { dashboardParams } from "@lib/utils/search-params-parsers";
import { OrganizationType } from "@models/organization";
import { Role } from "@models/role";
import { useCurrentUser } from "@src/hooks/queries/account";
import { useFeatures, useRole } from "@src/hooks/queries/account-selectors";
import {
  useClassrooms,
  useOrganizationDetails,
} from "@src/hooks/queries/organization";

import { RosterModal } from "@views/users/roster/roster-modal";
import {
  ClassroomsShortcuts,
  DashboardHeader,
  UploadFooter,
} from "./components";
import { ProductTabs } from "./views/single-org/product.tabs";

import "./dashboard.scss";

export default ({ className, responsiveStyles = {} }) => {
  const [{ from: breadcrumbLabel, product }] = useQueryStates(dashboardParams);
  const currentUser = useCurrentUser();
  const role = useRole();
  const features = useFeatures();
  const { organizationCode, userId, firstName } = currentUser || {};

  const { orgCode = organizationCode } = useParams();

  const { data: organization, isFetching: fetchingOrganization } =
    useOrganizationDetails(orgCode, {
      enabled: !!orgCode,
    });

  const [openRosterModal, setOpenRosterModal] = useState();
  const { canCreateUser } = features || {};

  const { data: classrooms = [] } = useClassrooms({
    userId,
    role: role === Role.INSTRUCTOR.id ? role : Role.ORG_ADMIN.id,
    orgCode: organization?.code,
  });

  const headerProps = useMemo(() => {
    const title =
      organization?.typeId === OrganizationType.SINGLE
        ? "Dashboard"
        : organization?.name;

    const children =
      organization?.typeId === OrganizationType.SINGLE ? (
        <div>
          Hi <strong>{firstName}</strong>! Here&apos;s your{" "}
          <strong>{organization?.name}</strong> update for{" "}
          {formatDateLocaleMonth()}.
        </div>
      ) : null;
    return { title, children };
  }, [organization, firstName]);

  return fetchingOrganization ? (
    <Loader fixed />
  ) : (
    <div className={clsx("dashboard", className)}>
      <div className="dashboard-container">
        <DashboardHeader
          title={
            <div className="dashboard-title-container">
              <span>{headerProps.title}</span>
              <FeedbackButton productId={product} />
            </div>
          }
          breadcrumbLabel={breadcrumbLabel}
          subtitle={headerProps.children}
          organizationType={organization?.typeId}
        />
        <ProductTabs
          products={organization?.products}
          professionalLearning={organization?.professionalLearning}
        />
        {canCreateUser && (
          <UploadFooter setOpenRosterModal={setOpenRosterModal} />
        )}
      </div>
      <div className="side-shortcuts">
        <ClassroomsShortcuts
          classrooms={classrooms}
          orgCode={organization?.code}
          products={organization?.products || []}
        ></ClassroomsShortcuts>
      </div>
      <RosterModal
        onClose={() => setOpenRosterModal(false)}
        show={openRosterModal}
        responsiveStyles={responsiveStyles}
      ></RosterModal>
    </div>
  );
};
